import { formatValidQueryParamUrlCommaSeparated, isBlank } from "utils/common";
import { formatSale } from "utils/sales.util";
import { createPaginatedEndpoint } from "utils/services.util";

import { emptySplitApi } from "./empty-split-api";
import {
  HCFAAttachmentInput,
  HCFAForm,
  HCFAFormInput
} from "./types/hcfa1500-forms.type";
import { CSVReportResponse } from "./types/reports.type";
import { SaleItem } from "./types/sale-items.type";
import {
  AccountReceivableSalesResponse,
  ItemInput,
  LineItem,
  PatientSalesFilters,
  Sale,
  SaleInput,
  SalesDownloadQuery,
  SalesFilters,
  SalesReportResponse,
  SalesResponse,
  type SalesTotalResponse
} from "./types/sales.type";

export const DEFAULT_SALES_LIMIT = 25;

export const SALES_DEFAULT_QUERY =
  "{id,balance,is_active_subscription,created_by,items{cpt_code,insurer_responsibility},patient{id,first_name,middle_name,last_name,preferred_name,title,suffix,quickbooks_customer_id},provider{id,first_name,last_name,title,suffix,is_active},is_direct_supervisor,supervising_provider{id,first_name,last_name,title,suffix,is_active},created_at,invoice_date,service_date,status,total,total_adjusted,total_paid,most_recent_hcfa_1500_form{id,status,form_data,is_uploaded},appointment{id,chart_note{icd10_codes}},quickbooks_invoice_id,quickbooks_sync{sync_status,error},clinic{id,name,logo,practice{id,name,logo}},patient_responsibility_balance,insurer_responsibility_balance,total_refunds,payment_source_type,managed_care_plan,insurance_policy{id,insurer{id,name,attachments_allowed}},secondary_insurance_policy{id,insurer{id,name,attachments_allowed}},assigned_tags{id,tag,description,expires_at,created_by,updated_by,created_at,updated_at},is_migrated,migration_source_id,collection{id,status,original_balance,current_balance}}";

export const getSalesQuery = (enableDarwillIntegration?: boolean) => {
  if (enableDarwillIntegration) {
    return `${SALES_DEFAULT_QUERY.slice(0, -1)},darwill_invoice_file_url,darwill_sent_date}`;
  }
  return SALES_DEFAULT_QUERY;
};

export const PATIENT_SALES_QUERY =
  "{id,balance,payment_source_type,insurance_policy{id,insurer{id,name,payer_name,attachments_allowed}},secondary_insurance_policy{id,insurer{id,name,payer_name,attachments_allowed}},managed_care_plan{id,name,managed_care_provider{id,name}},provider{id,first_name,last_name,title,suffix,full_name,npi,is_active},created_at,invoice_date,service_date,status,total,total_adjusted,total_paid,most_recent_hcfa_1500_form{id,status,form_data,is_uploaded},appointment{id,type{id,name,default_duration},chart_note{icd10_codes}},quickbooks_invoice_id,quickbooks_sync{sync_status,error},clinic{phone,city,quickbooks_token,review_link,name,card_processing_provider,justifi_account_id,tilled_account_id,id,state,zip_code,region,country,extra{id,billng_street_address_1,billng_street_address_2,billng_city,billng_state,billng_country,billng_zip,billng_phone},quickbooks_realm_id,google_place_id,practice{id,name,logo,card_processing_provider,justifi_account_id,tilled_account_id},is_active,logo,timezone,display_name,sms_phone,street_address_1,street_address_2},patient_responsibility_balance,insurer_responsibility_balance,total_refunds,items{id,cpt_code{id,code},patient_responsibility,total,insurer_responsibility,insurers{id,content_type,object_id,amount}},created_by,updated_by,insurer_responsibility_total,patient_responsibility_total,patient{id,full_name,quickbooks_customer_id},is_migrated}";

const GET_SALE_QUERY =
  "{id,patient{id,full_name,first_name,middle_name,last_name,preferred_name,title,suffix,phone,email,birthdate,quickbooks_customer_id,is_active,street_address_1,street_address_2,city,state,zip_code},service_date,created_at,invoice_date,icd10_codes,appointment{id,type{id,name},start_moment,end_moment,status,chart_note{icd10_codes},timer{id,status,status_display,minutes_accumulated,last_started_at,completed}},provider{title,last_name,is_active,suffix,photo,signature,first_name,role,id,npi,license_number},is_direct_supervisor,supervising_provider{title,last_name,is_active,suffix,photo,signature,first_name,role,id,npi,license_number},clinic{phone,ein,city,quickbooks_token,review_link,name,card_processing_provider,justifi_account_id,tilled_account_id,id,npi,state,zip_code,region,country,extra{id,billng_street_address_1,billng_street_address_2,billng_city,billng_state,billng_country,billng_zip,billng_phone},quickbooks_realm_id,google_place_id,practice{id,name,logo,card_processing_provider,justifi_account_id,tilled_account_id},is_active,logo,timezone,display_name,sms_phone,street_address_1,street_address_2},total,total_paid,total_adjusted,balance,is_balanced,status,notes,patient_responsibility_total,insurer_responsibility_total,patient_responsibility_total_paid,insurer_responsibility_total_paid,patient_responsibility_total_adjusted,insurer_responsibility_total_adjusted,patient_responsibility_total_refunds,insurer_responsibility_total_refunds,patient_responsibility_balance,insurer_responsibility_balance,patient_responsibility_balance_field,insurer_responsibility_balance_field,quickbooks_invoice_id,quickbooks_sync{sync_status,error},total_refunds,payment_source_type,managed_care_plan{id,name,notes,managed_care_provider{id,name}},insurance_policy{id,policyholder_member_id,plan_name,is_active,type,hearing_benefit,deductible,copay,has_hearing_aid,policyholder_relationship,policyholder_first_name,policyholder_last_name,policyholder_middle_name,policyholder_sex,policyholder_birthdate,policyholder_employer_name,policyholder_group_number,policyholder_street_address_1,policyholder_street_address_2,policyholder_city,policyholder_state,policyholder_country,policyholder_zip_code,policyholder_phone,notes,insurance_card_front,insurance_card_back,insurer_phone_number,coverage_type,insurer{id,name,payer_id,payer_name,attachments_allowed},effective_date,expiration_date,policy_type},secondary_insurance_policy{id,policyholder_member_id,plan_name,is_active,type,hearing_benefit,deductible,copay,has_hearing_aid,policyholder_relationship,policyholder_first_name,policyholder_last_name,policyholder_middle_name,policyholder_sex,policyholder_birthdate,policyholder_employer_name,policyholder_group_number,policyholder_street_address_1,policyholder_street_address_2,policyholder_city,policyholder_state,policyholder_country,policyholder_zip_code,policyholder_phone,notes,insurance_card_front,insurance_card_back,insurer_phone_number,coverage_type,insurer{id,name,payer_id,payer_name,attachments_allowed},effective_date,expiration_date,policy_type},most_recent_hcfa_1500_form{id,status},assigned_tags,items{id,patient_responsibility,total,insurer_responsibility,insurers{id,content_type,object_id,amount}},created_by,refunds{id,payer_type},collection{id,status},is_migrated}";

const GET_HCFA1500_FORMS_QUERY =
  "{id,status,created_by,is_assignment_accepted,is_uploaded,uploaded_at,form_data,era_claims{id},primary_insurance_policy{id,insurer{id,attachments_allowed}}}";

// Product.cpt_codes is gated behind multipleCptCodesFeature to avoid querying
// a field the BE may not expose yet (deploy-ordering safety).
const getSaleItemsQuery = (multipleCptCodesFeature?: boolean): string => {
  const cptCodes = multipleCptCodesFeature
    ? ",cpt_codes{id,code,description}"
    : "";
  return `{id,sort_order,discount_type{id,is_active,type,subtype,product{id,type,display_name,master_product{subtype}},mode,value,title},cpt_code{id,code,description},description,exchanged_at,returned_at,discount,insurer_responsibility,patient_responsibility,price,price_table_item{id,price,max_discount,is_active,price_table{id,name}},inventory_product{id,status,serial_number,condition,loanable,demoable,specification,manufacturer_warranty_expiration_date,available_quantity},patient_products{id,status,inventory_product{id,status,serial_number,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date,condition,checked_in_at,ear,specification,is_active,quantity,ordered_quantity,reserved_quantity,loanable,demoable}},product{id,display_name,max_discount,cpt_code{id,code,description}${cptCodes},master_product{id,type,subtype,specification}},quantity,quickbooks_line_id,tax_rate,taxable,total,insurers{amount,balance,id,insurer{id}},created_at,created_by,updated_by,delivered_at,order_item{id,patient_product{id,serial_number,ear},inventory_product{id,serial_number,product{id,display_name}}},order{id,type,clinic_order_number,status},claim_form_data,is_migrated}`;
};

export const STATEMENT_QUERY =
  "{id,appointment{type{name}},service_date,status,balance,patient_responsibility_total,insurer_responsibility_total,patient_responsibility_balance,insurer_responsibility_balance,hcfa1500_forms{id,uploaded_at,status,is_uploaded,primary_insurance_policy{id,insurer{id,name,attachments_allowed}}},adjustments{id,amount,description,adjustment_date,payer_type},items{id,billable,patient_responsibility,insurer_responsibility,description,total,taxable,tax_rate,quantity,price,patient_products{id,status},product{id,display_name,master_product{type,subtype}},cpt_code{id,code,description},exchanged_at,returned_at},refunds{id,refunded_at,payer_type,amount,description,reason},payments{id,amount,method,payer_type,payment_date,description},total,patient{id,first_name,last_name}}";

// Maps FE ordering fields that rely on queryset annotations (via NullsOrderingFilter)
// to valid DB fields for CSV exports, which bypass the filter and call order_by() directly.
const CSV_ORDERING_MAP: Record<string, string | null> = {
  patient_name: "patient__last_name",
  claim_status: null
};

const mapSaleCsvOrdering = (ordering?: string): string | undefined => {
  if (!ordering) return undefined;
  return (
    ordering
      .split(",")
      .map((field) => {
        const desc = field.startsWith("-");
        const name = desc ? field.slice(1) : field;
        const mapped = name in CSV_ORDERING_MAP ? CSV_ORDERING_MAP[name] : name;
        if (!mapped) return null;
        return desc ? `-${mapped}` : mapped;
      })
      .filter(Boolean)
      .join(",") || undefined
  );
};

export const getDefaultSalesFilters = (filters: SalesFilters) => {
  const { customFilters, baseFilter } = filters;

  return {
    id: filters.id,
    discarded: filters.discarded,
    before_service_date: filters.before_service_date,
    after_service_date: filters.after_service_date,
    before_delivery_date: filters.before_delivery_date,
    after_delivery_date: filters.after_delivery_date,
    after_discarded_at: filters.after_discarded_at,
    before_discarded_at: filters.before_discarded_at,
    assigned_tags: customFilters?.assigned_tags,
    payment_source_type: customFilters?.payment_source_type,
    patient: customFilters?.patient,
    provider: customFilters?.provider,
    hcfa1500_form_status: customFilters?.hcfa1500FormStatus,
    status: customFilters?.status,
    clinic: customFilters?.clinic,
    region: customFilters?.region,
    sale_age: customFilters?.sale_age,
    taxable: customFilters?.taxable,
    product_type: customFilters?.product_type,
    product_subtype: customFilters?.product_subtype,
    is_returned: customFilters?.is_returned,
    service_date: customFilters?.service_date,
    ...filters.baseFilter,
    excluded_status: customFilters?.excluded_status
      ? customFilters?.excluded_status
      : baseFilter?.excluded_status || undefined,
    is_future_sale:
      filters.is_future_sale ?? (filters.baseFilter as any)?.is_future_sale,
    is_balanced:
      filters.is_balanced ?? (filters.baseFilter as any)?.is_balanced,
    exclude_future_sale:
      filters.exclude_future_sale ??
      (filters.baseFilter as any)?.exclude_future_sale,
    appointment: filters.appointment,
    has_negative_balance: filters.has_negative_balance,
    has_negative_balance_accounts_receivable:
      filters.has_negative_balance_accounts_receivable,
    has_patient_negative_balance: !isBlank(filters.has_patient_negative_balance)
      ? filters.has_patient_negative_balance
      : customFilters?.has_patient_negative_balance,
    has_outstanding_patient_balance:
      customFilters?.has_outstanding_patient_balance,
    has_outstanding_insurer_balance:
      customFilters?.has_outstanding_insurer_balance,
    query: filters.query ? filters.query : PATIENT_SALES_QUERY,
    ordering: filters.ordering || "-service_date,-pk",
    opening_balance: filters.opening_balance,
    search: filters.search && encodeURIComponent(filters.search),
    tag_description: filters.tag_description,
    snapshot: filters.snapshot,
    has_refunds: filters.has_refunds,
    nulls_first: filters.nulls_first,
    is_qb_synced: customFilters?.is_qb_synced,
    insurer: customFilters?.insurer,
    primary_insurer: customFilters?.primary_insurer,
    managed_care_plan: customFilters?.managed_care_plan,
    managed_care_provider: customFilters?.managed_care_provider,
    has_active_subscription: true,
    include_totals: filters?.include_totals,
    has_insurer_negative_balance: customFilters?.has_insurer_negative_balance,
    optimize_ar: filters.optimize_ar
  };
};

export const salesApi: any = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getSalesPaginated: createPaginatedEndpoint(builder, {
      queryFn: (filters) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated({
          ...getDefaultSalesFilters(filters),
          limit: filters.limit ?? DEFAULT_SALES_LIMIT,
          offset: filters.offset ?? 0
        });

        return `/sales/?${filtersString}&nulls_first=true`;
      },
      providesTags: ["Paginated-Sales"],
      transformResponseFn: (
        response: SalesResponse | AccountReceivableSalesResponse
      ) =>
        ("data" in response ? response.data.results : response.results)?.map(
          (sale) => formatSale(sale)
        )
    }),
    querySales: builder.query<Sale[] | SalesReportResponse, SalesFilters>({
      query: (filters) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated({
          ...getDefaultSalesFilters(filters),

          limit: filters.limit ?? DEFAULT_SALES_LIMIT,
          offset: filters.offset ?? 0
        });

        return `/sales/?${filtersString}&nulls_first=true`;
      },
      providesTags: ["Sales"],
      transformResponse: (
        response:
          | SalesResponse
          | AccountReceivableSalesResponse
          | SalesReportResponse
      ) => {
        if ("total" in response) {
          return response;
        }

        if ("data" in response) {
          return response.data.results?.map((sale) =>
            formatSale(sale, response.overview)
          );
        }

        return response.results?.map((sale) => formatSale(sale));
      }
    }),
    querySalesCounter: builder.query<
      SalesTotalResponse,
      Omit<SalesFilters, "query" | "limit" | "offset"> & {
        includeTotals?: boolean;
      }
    >({
      query: ({ includeTotals, ...filters }) =>
        `/sales/${
          includeTotals ? "total" : "count"
        }/?${formatValidQueryParamUrlCommaSeparated({
          ...filters.baseFilter
        })}`,
      transformResponse: (response: SalesTotalResponse | string, meta, arg) => {
        if (arg.includeTotals && typeof response === "object") {
          return response as SalesTotalResponse;
        } else {
          return {
            count: Number(response || 0),
            total_amount: undefined,
            total_balance: undefined,
            total_balance_of_uncompleted_sales: undefined
          };
        }
      },
      providesTags: ["Sales"]
    }),
    getPatientSales: builder.query<Sale[], PatientSalesFilters>({
      query: ({ query = PATIENT_SALES_QUERY, ...filters }) =>
        `/sales/?${formatValidQueryParamUrlCommaSeparated(
          filters
        )}&query=${query}&nulls_first=true`,
      providesTags: ["Sales"],
      transformResponse: (response: SalesResponse) =>
        response.results.map((sale) => formatSale(sale))
    }),
    getPatientSalesPaginated: createPaginatedEndpoint(builder, {
      queryFn: ({ query = PATIENT_SALES_QUERY, ...filters }) =>
        `/sales/?${formatValidQueryParamUrlCommaSeparated(
          filters
        )}&query=${query}&nulls_first=true`,
      providesTags: ["Paginated-Sales"],
      transformResponseFn: (response: SalesResponse) =>
        response.results.map((sale) => formatSale(sale))
    }),
    getSale: builder.query<
      Sale,
      { saleId: number; query?: string; timestamp?: string }
    >({
      query: ({ saleId, query = GET_SALE_QUERY }) =>
        `/sales/${saleId}/?query=${query}`,
      providesTags: ["Sales"],
      transformResponse: formatSale
    }),
    getSaleBalance: builder.query<
      {
        id: number;
        is_balanced: boolean;
        balance: string;
        service_date: string | null;
        status: number;
      },
      number
    >({
      query: (saleId) =>
        `/sales/${saleId}/?query={id,balance,is_balanced,service_date,status}`
    }),
    getSaleItems: builder.query<
      SaleItem[],
      { saleId: number; multipleCptCodesFeature?: boolean }
    >({
      query: ({ saleId, multipleCptCodesFeature }) => {
        if (!Number.isFinite(saleId) || saleId <= 0) {
          throw new Error(`getSaleItems: invalid saleId ${saleId}`);
        }
        return `/sales/${saleId}/items/?query=${getSaleItemsQuery(multipleCptCodesFeature)}`;
      },
      providesTags: ["SaleItems"]
    }),
    getSaleItemCosts: builder.query<
      {
        id: number | string;
        cost?: string;
        inventory_product?: { id: number; supplier_price?: string } | null;
      }[],
      { saleId: number }
    >({
      query: ({ saleId }) =>
        `/sales/${saleId}/items/?query={id,cost,inventory_product{id,supplier_price}}`,
      providesTags: ["SaleItemCosts"]
    }),
    getHCFAForms: builder.query<HCFAForm[], number>({
      query: (saleId) =>
        `/sales/${saleId}/hcfa1500-forms/?query=${GET_HCFA1500_FORMS_QUERY}`,
      providesTags: ["HCFA1500Forms"]
    }),
    addSale: builder.mutation<Sale, SaleInput>({
      query: (sale) => ({
        url: "/sales/",
        method: "POST",
        body: sale
      }),
      invalidatesTags: ["Sales", "Patient", "Patients"]
    }),
    updateSale: builder.mutation<Sale, Partial<SaleInput>>({
      query: ({ id, ...data }) => ({
        url: `/sales/${id}/`,
        method: "PATCH",
        body: data
      }),
      invalidatesTags: ["Sales", "Patient", "Patients"]
    }),
    deleteSale: builder.mutation<Sale, number>({
      query: (saleId) => ({
        url: `/sales/${saleId}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["Sales", "Patient", "Patients"]
    }),
    fileSaleHCFA: builder.mutation<HCFAForm, HCFAFormInput>({
      query: ({ saleId, ...hcfaFormFData }) => ({
        url: `/sales/${saleId}/hcfa1500-forms/`,
        method: "POST",
        body: hcfaFormFData
      }),
      invalidatesTags: ["Sales", "HCFA1500Forms"]
    }),
    updateHCFAFileSale: builder.mutation<HCFAForm, HCFAFormInput>({
      query: ({ id, saleId, ...hcfaFormFData }) => ({
        url: `/sales/${saleId}/hcfa1500-forms/${id}/`,
        method: "PATCH",
        body: hcfaFormFData
      }),
      invalidatesTags: ["Sales", "HCFA1500Forms"]
    }),
    uploadHCFAAttachment: builder.mutation<HCFAForm, HCFAAttachmentInput>({
      query: ({ id, saleId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/sales/${saleId}/hcfa1500-forms/${id}/upload-attachment/`,
          method: "POST",
          body: formData
        };
      },
      invalidatesTags: ["Sales", "HCFA1500Forms"]
    }),
    addSaleItem: builder.mutation<LineItem, ItemInput>({
      query: ({ saleId, item: { id, ...itemData } }) => ({
        url: `/sales/${saleId}/items/`,
        method: "POST",
        body: itemData
      }),
      invalidatesTags: [
        "Sales",
        "SaleItems",
        "Patient",
        "Patients",
        "MergeTagsSale"
      ]
    }),
    updateSaleItem: builder.mutation<LineItem, ItemInput>({
      query: ({ saleId, item: { id, ...itemData } }) => ({
        url: `/sales/${saleId}/items/${id}/`,
        method: "PATCH",
        body: itemData
      }),
      invalidatesTags: [
        "Sales",
        "SaleItems",
        "Patient",
        "Patients",
        "MergeTagsSale"
      ]
    }),
    deleteSaleItem: builder.mutation<LineItem, ItemInput>({
      query: ({ saleId, item }) => ({
        url: `/sales/${saleId}/items/${item.id}/`,
        method: "DELETE",
        body: item
      }),
      invalidatesTags: [
        "Sales",
        "Patient",
        "Patients",
        "SaleItems",
        "MergeTagsSale"
      ]
    }),
    postQuickbooksInvoice: builder.mutation<any, number>({
      query: (saleId) => ({
        url: `/sales/${saleId}/sync/`,
        method: "POST"
      }),
      invalidatesTags: ["Sales", "SaleItems"]
    }),
    postQuickbooksLineItemsSync: builder.mutation<any, number>({
      query: (saleId) => ({
        url: `/sales/${saleId}/quickbooks-sync/`,
        method: "POST"
      }),
      invalidatesTags: ["Sales", "SaleItems"]
    }),
    downloadSaleCSV: builder.query<CSVReportResponse, SalesDownloadQuery>({
      query: (filters) => ({
        url: `/sales/`,
        headers: { Accept: "custom-csv/json" },
        params: {
          ...filters,
          ordering: mapSaleCsvOrdering(filters?.ordering) || "-service_date,-pk"
        }
      })
    }),
    downloadAdpXml: builder.query<void, { saleId: number; fileName: string }>({
      query: ({ saleId }) => ({
        url: `/sales/${saleId}/adp/`,
        headers: {
          "Content-Type": "application/xml"
        },
        responseHandler: async (response: any) => {
          return await response.text();
        }
      }),
      transformResponse: (xmlText: string, meta, { fileName }) => {
        const downloadLink = document.createElement("a");
        downloadLink.setAttribute("target", "_blank");
        downloadLink.href = URL.createObjectURL(
          new Blob([xmlText], { type: "application/xml" })
        );

        downloadLink.download = fileName;

        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    }),
    markSalesAsCompleted: builder.mutation<
      void,
      { before_service_date: string; ignore_balance?: boolean }
    >({
      query: (params) => ({
        url: `/sales/mark-as-completed/`,
        method: "POST",
        params
      }),
      invalidatesTags: ["Sales", "Patient", "Patients"]
    }),
    moveSaleItem: builder.mutation<
      LineItem,
      { saleItemId: number; saleId: number }
    >({
      query: ({ saleItemId, saleId }) => ({
        url: `/sale-items/${saleItemId}/`,
        method: "PATCH",
        body: { sale: saleId }
      }),
      invalidatesTags: [
        "Sales",
        "SaleItems",
        "Patient",
        "Patients",
        "MergeTagsSale"
      ]
    }),
    getSaleQuickbooksSync: builder.query<
      { id: number; quickbooks_sync: { sync_status: string } },
      number
    >({
      query: (saleId) =>
        `/sales/${saleId}/?query={id,quickbooks_invoice_id,quickbooks_sync{sync_status,error}}`,
      providesTags: ["Sales", "SalesQuickBooksSync"]
    }),
    linkQuickBooksInvoice: builder.mutation<
      { status: string; quickbooks_invoice_id: string },
      { saleId: number; quickbooks_invoice_id: string }
    >({
      query: ({ saleId, quickbooks_invoice_id }) => ({
        url: `/sales/${saleId}/link-quickbooks-invoice/`,
        method: "POST",
        body: { quickbooks_invoice_id }
      }),
      invalidatesTags: ["Sales", "SalesQuickBooksSync", "Paginated-Sales"]
    }),
    sendToDarwill: builder.mutation<
      { status: string; created_count: number },
      {
        sale_ids: number[];
        invoice_memo?: string;
        include_icd10_codes?: boolean;
        bill_to_responsible_party?: boolean;
        billing_address_clinic?: number;
        include_qr_code?: boolean;
        include_sale_notes?: boolean;
      }
    >({
      query: (data) => ({
        url: `/sales/send-to-darwill/`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["Sales", "Paginated-Sales"]
    }),
    undiscardSale: builder.mutation<
      { id: number; restored: boolean; warnings: string[] },
      number
    >({
      query: (id) => ({ url: `/sales/${id}/undiscard/`, method: "POST" }),
      invalidatesTags: [
        "Sales",
        "Paginated-Sales",
        "Patient",
        "Patients",
        "Adjustments",
        "Refunds Report"
      ]
    }),
    bulkSendToCollections: builder.mutation<
      {
        enrolled_count: number;
        skipped_invalid_status: number;
        skipped_no_balance: number;
        skipped_already_enrolled: number;
        error_count: number;
      },
      | { sale_ids: number[]; cadence?: number }
      | { filters: Record<string, unknown>; cadence?: number }
    >({
      query: (body) => ({
        url: `/collections/bulk-create/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["Sales", "Paginated-Sales", "Collections"]
    })
  })
});

export const {
  useQuerySalesQuery,
  useLazyQuerySalesQuery,
  useGetSalesPaginatedQuery,
  useLazyDownloadSaleCSVQuery,
  useLazyQuerySalesCounterQuery,
  useGetPatientSalesQuery,
  useGetSaleItemsQuery,
  useLazyGetSaleItemsQuery,
  useGetHCFAFormsQuery,
  useAddSaleMutation,
  useUpdateSaleMutation,
  useDeleteSaleMutation,
  useGetSaleQuery,
  useLazyGetSaleQuery,
  useLazyGetSaleBalanceQuery,
  useFileSaleHCFAMutation,
  useUpdateHCFAFileSaleMutation,
  useUploadHCFAAttachmentMutation,
  useAddSaleItemMutation,
  useUpdateSaleItemMutation,
  useDeleteSaleItemMutation,
  usePostQuickbooksLineItemsSyncMutation,
  usePostQuickbooksInvoiceMutation,
  useLazyDownloadAdpXmlQuery,
  useMarkSalesAsCompletedMutation,
  useMoveSaleItemMutation,
  useGetSaleQuickbooksSyncQuery,
  useLazyGetSaleQuickbooksSyncQuery,
  useLinkQuickBooksInvoiceMutation,
  useSendToDarwillMutation,
  useBulkSendToCollectionsMutation,
  useUndiscardSaleMutation,
  useLazyGetSaleItemCostsQuery
} = salesApi;
